import { useEffect, useRef, useState } from 'react';

export interface UseInViewOptions {
  /** Fraction of the element that must be visible to trigger. */
  threshold?: number;
  /** Shrinks the viewport so the trigger fires slightly before the edge. */
  rootMargin?: string;
  /** Stop observing after the first intersection (the usual case). */
  once?: boolean;
}

/**
 * Reports whether an element has scrolled into the viewport.
 *
 * This gates whether content is *visible*, so it is written to fail open —
 * being wrong in the direction of "hidden" means a blank section:
 *
 *  - no IntersectionObserver → visible immediately
 *  - scrolled past without an intersection being delivered → visible
 *  - a fast scroll that skips the element entirely → caught by a scroll
 *    fallback, because intersections are delivered asynchronously and a
 *    jump (End key, a flick, a tool driving the page) can outrun them
 */
export function useInView<T extends HTMLElement = HTMLDivElement>({
  threshold = 0.15,
  rootMargin = '0px 0px -10% 0px',
  once = true,
}: UseInViewOptions = {}) {
  const ref = useRef<T>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    if (typeof IntersectionObserver === 'undefined') {
      setIsInView(true);
      return;
    }

    let settled = false;
    let frame = 0;

    const cleanUp = () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(frame);
    };

    const reveal = () => {
      if (settled) return;
      // `settled` only latches for a one-shot observer. Latching it for a
      // repeating one would leave the element stuck hidden the first time it
      // scrolled back out, since the branch below can still set false.
      if (once) {
        settled = true;
        cleanUp();
      }
      setIsInView(true);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          // An element that has scrolled entirely above the viewport has been
          // seen, even though `isIntersecting` is false.
          if (entry.isIntersecting || entry.boundingClientRect.bottom < 0) {
            reveal();
          } else if (!once) {
            setIsInView(false);
          }
        }
      },
      { threshold, rootMargin },
    );

    observer.observe(element);

    // Safety net for jumps the observer never gets to report on.
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const rect = element.getBoundingClientRect();
        const entered = rect.top < window.innerHeight && rect.bottom > 0;
        if (entered || rect.bottom <= 0) reveal();
      });
    };

    if (once) {
      window.addEventListener('scroll', onScroll, { passive: true });
    }

    return cleanUp;
  }, [threshold, rootMargin, once]);

  return { ref, isInView };
}
